import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Check } from 'lucide-react';
import { MessageModal } from '../../components/modals/MessageModal';
export function SignupPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    userType: 'buyer',
    agreeToTerms: false
  });
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    type: 'info' as 'success' | 'error' | 'info'
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.agreeToTerms) {
      setModalContent({
        title: 'Terms Required',
        message: 'Please agree to the Terms of Use and Privacy Policy to continue.',
        type: 'error'
      });
      setIsMessageModalOpen(true);
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setModalContent({
        title: 'Password Mismatch',
        message: 'Passwords do not match. Please try again.',
        type: 'error'
      });
      setIsMessageModalOpen(true);
      return;
    }
    setModalContent({
      title: 'Account Created',
      message: 'Your account has been created successfully. Please check your email for OTP verification.',
      type: 'success'
    });
    setIsMessageModalOpen(true);
    setTimeout(() => {
      navigate('/auth/verify-otp', {
        state: {
          email: formData.email
        }
      });
    }, 2000);
  };
  const handleSocialLogin = (provider: string) => {
    setModalContent({
      title: `${provider} Login`,
      message: `Redirecting to ${provider} authentication... In production, this would use OAuth 2.0 flow.`,
      type: 'info'
    });
    setIsMessageModalOpen(true);
  };
  return <div data-testid="signup-page" className="min-h-screen bg-[#f5f5f7] font-sans text-[#1f2933]" data-id="element-2279">
      <header data-testid="signup-header" className="bg-white border-b border-gray-300 py-4 px-6 md:px-12" data-id="element-2280">
        <Link data-testid="signup-logo-link" to="/" className="flex items-center gap-2" data-id="element-2281">
          <div className="w-8 h-8 bg-[#1f2933] rounded-sm" data-id="element-2282"></div>
          <span className="font-bold text-xl tracking-tight" data-id="element-2283">
            Citiplots.com
          </span>
        </Link>
      </header>

      <main data-testid="signup-main" className="max-w-md mx-auto px-4 py-12" data-id="element-2284">
        <div data-testid="signup-card" className="bg-white border border-gray-300 p-8 shadow-sm" data-id="element-2285">
          <h1 data-testid="signup-title" className="text-2xl font-bold mb-2" data-id="element-2286">
            Create Account
          </h1>
          <p data-testid="signup-subtitle" className="text-sm text-gray-500 mb-6" data-id="element-2287">
            Join India's direct plot transaction platform
          </p>

          <form data-testid="signup-form" onSubmit={handleSubmit} className="space-y-4" data-id="element-2288">
            <div data-id="element-2289">
              <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-2290">
                I am a
              </label>
              <div data-testid="signup-user-type-selector" className="grid grid-cols-2 gap-2" data-id="element-2291">
                {[{
                value: 'buyer',
                label: 'Buyer'
              }, {
                value: 'seller',
                label: 'Seller'
              }, {
                value: 'developer',
                label: 'Developer'
              }, {
                value: 'nri',
                label: 'NRI'
              }].map(type => <button key={type.value} data-testid={`signup-user-type-${type.value}-btn`} type="button" onClick={() => setFormData({
                ...formData,
                userType: type.value
              })} className={`p-3 border text-sm font-medium ${formData.userType === type.value ? 'border-[#1f2933] bg-gray-50' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-2292">
                    {type.label}
                  </button>)}
              </div>
            </div>

            <div data-id="element-2293">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2294">
                Full Name
              </label>
              <input data-testid="signup-name-input" type="text" required value={formData.fullName} onChange={e => setFormData({
              ...formData,
              fullName: e.target.value
            })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-2295" />
            </div>

            <div data-id="element-2296">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2297">
                Email Address
              </label>
              <input data-testid="signup-email-input" type="email" required value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-2298" />
            </div>

            <div data-id="element-2299">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2300">
                Phone Number
              </label>
              <input data-testid="signup-phone-input" type="tel" required placeholder="+91" value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-2301" />
            </div>

            <div data-id="element-2302">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2303">
                Password
              </label>
              <div className="relative" data-id="element-2304">
                <input data-testid="signup-password-input" type={showPassword ? 'text' : 'password'} required value={formData.password} onChange={e => setFormData({
                ...formData,
                password: e.target.value
              })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-2305" />
                <button data-testid="signup-password-toggle-btn" type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2306">
                  {showPassword ? <EyeOff className="w-4 h-4" data-id="element-2307" /> : <Eye className="w-4 h-4" data-id="element-2308" />}
                </button>
              </div>
            </div>

            <div data-id="element-2309">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2310">
                Confirm Password
              </label>
              <input data-testid="signup-confirm-password-input" type="password" required value={formData.confirmPassword} onChange={e => setFormData({
              ...formData,
              confirmPassword: e.target.value
            })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-2311" />
            </div>

            <div className="flex items-start gap-2" data-id="element-2312">
              <input data-testid="signup-terms-checkbox" type="checkbox" id="terms" checked={formData.agreeToTerms} onChange={e => setFormData({
              ...formData,
              agreeToTerms: e.target.checked
            })} className="mt-1" data-id="element-2313" />
              <label htmlFor="terms" className="text-xs text-gray-600" data-id="element-2314">
                I agree to the{' '}
                <Link to="/terms" className="text-blue-600 underline" data-id="element-2315">
                  Terms of Use
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-blue-600 underline" data-id="element-2316">
                  Privacy Policy
                </Link>
              </label>
            </div>

            <button data-testid="signup-submit-btn" type="submit" className="w-full py-3 bg-[#1f2933] text-white font-bold hover:bg-gray-800" data-id="element-2317">
              Create Account
            </button>
          </form>

          <div data-testid="signup-social-section" className="mt-6" data-id="element-2318">
            <div className="relative" data-id="element-2319">
              <div className="absolute inset-0 flex items-center" data-id="element-2320">
                <div className="w-full border-t border-gray-300" data-id="element-2321"></div>
              </div>
              <div className="relative flex justify-center text-xs" data-id="element-2322">
                <span className="bg-white px-2 text-gray-500" data-id="element-2323">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2" data-id="element-2324">
              {['Google', 'LinkedIn', 'Facebook'].map(provider => <button key={provider} data-testid={`signup-${provider.toLowerCase()}-btn`} type="button" onClick={() => handleSocialLogin(provider)} className="py-2 border border-gray-300 text-xs font-medium hover:bg-gray-50" data-id="element-2325">
                  {provider}
                </button>)}
            </div>
          </div>

          <p data-testid="signup-signin-link" className="mt-6 text-center text-sm text-gray-600" data-id="element-2326">
            Already have an account?{' '}
            <Link to="/auth/signin" className="text-blue-600 font-bold hover:underline" data-id="element-2327">
              Sign In
            </Link>
          </p>
        </div>
      </main>

      <MessageModal isOpen={isMessageModalOpen} onClose={() => setIsMessageModalOpen(false)} title={modalContent.title} message={modalContent.message} type={modalContent.type} data-id="element-2328" />
    </div>;
}